'use client'

import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'

import { useBookingExternal } from '@booking/state/external.context'
import { formatMainLink, type Locale } from '@/src/lib/link'
import Link from 'next/link'
import styles from './index.module.scss'
import CheckBoxField from '@/src/components/BookTickets/components/FormFields/CheckboxField'

// ---------------------------------------------------------------------------

type TermsSectionProps = {
  sessionAccepted: boolean
  termsAccepted: boolean
  onSessionToggle: () => void
  onTermsToggle: () => void
}

export default function TermsSection({
  sessionAccepted,
  termsAccepted,
  onSessionToggle,
  onTermsToggle,
}: TermsSectionProps) {
  const t = useTranslations('booking')
  const { externalData } = useBookingExternal()
  const params = useParams<{ locale?: string }>()
  const locale = (params?.locale ?? 'pt') as Locale

  const termsHref = formatMainLink(externalData?.termsPath ?? 'terms')
  const bothAccepted = termsAccepted //&& sessionAccepted

  const termsLink = (chunks: React.ReactNode) => (
    <Link
      href={termsHref}
      target="_blank"
      className={styles.termsLink}
      onClick={(e) => e.stopPropagation()}
    >
      {chunks}
    </Link>
  )

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>{t('ticket.payment.terms.title')}</h2>

      <div className={styles.card}>
        {/* Row 1 — session terms */}
        {/*<CheckBoxField
          label={t.rich('ticket.customerDetails.termsAgree', {
            termsLink: (chunks) => (
              <Link
                href={formatMainLink(externalData?.termsPath ?? 'terms', locale)}
                target="_blank"
                className="underline"
              >
                {chunks}
              </Link>
            ),
          })}
          name="terms"
          checked={sessionAccepted}
          onChange={onSessionToggle}
        />*/}

        {/*<CheckboxRow accepted={sessionAccepted} onToggle={onSessionToggle}>
          {t.rich('ticket.customerDetails.sessionTerms', { termsLink })}{' '}
          <span className={styles.required}>*</span>
        </CheckboxRow>*/}

        {/*<div className={styles.divider} />*/}

        {/* Row 2 — terms & conditions */}
        <CheckboxRow accepted={termsAccepted} onToggle={onTermsToggle}>
          {t.rich('ticket.customerDetails.termsAgree', { termsLink })}{' '}
          <span className={styles.required}>*</span>
        </CheckboxRow>

        {/* Note — visible until both are ticked */}
        {!bothAccepted && (
          <p className={styles.error}>
            <InfoIcon />
            {t('ticket.payment.terms.error')}
          </p>
        )}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------

type CheckboxRowProps = {
  accepted: boolean
  onToggle: () => void
  children: React.ReactNode
}

function CheckboxRow({ accepted, onToggle, children }: CheckboxRowProps) {
  return (
    <button type="button" className={styles.row} onClick={onToggle}>
      <span className={cn(styles.checkbox, accepted && styles.checkboxChecked)}>
        {accepted && (
          <svg
            width="10"
            height="8"
            viewBox="0 0 10 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 4L3.5 6.5L9 1"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span className={styles.label}>{children}</span>
    </button>
  )
}

// ---------------------------------------------------------------------------

function InfoIcon() {
  return (
    <svg style={{ flexShrink: 0 }} width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 11.5a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm.75-3.75a.75.75 0 0 1-1.5 0v-3a.75.75 0 0 1 1.5 0v3z" />
    </svg>
  )
}
